import Keycloak from 'keycloak-js';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

export interface IGebruiker {
  naam: string;
  organisatie: string;
}

export const initialGebruiker: IGebruiker = {
  naam: '',
  organisatie: '',
};

export interface IAuthentication {
  authenticated: boolean;
  authorised: boolean;
  busyAuthenticating: boolean;
  rollen: string[];
  gebruiker: IGebruiker;
}

export const initialAuthentication: IAuthentication = {
  authenticated: false,
  authorised: false,
  busyAuthenticating: true,
  rollen: [],
  gebruiker: initialGebruiker,
};

export const keycloak = new Keycloak({
  url: window.APP_CONFIG.authServerUrl,
  realm: 'ovam',
  clientId: 'iris-ui',
});

interface IOidcSession {
  token: string;
  refreshToken: string;
  idToken: string;
}

class OidcSession {
  private static readonly attribute: string = 'oidc';

  static readonly get = (): IOidcSession => {
    const oidcString: string | null = localStorage.getItem(OidcSession.attribute);
    return oidcString && JSON.parse(oidcString);
  };

  static readonly put = (): void => {
    localStorage.setItem(
      OidcSession.attribute,
      JSON.stringify({
        token: keycloak.token,
        refreshToken: keycloak.refreshToken,
        idToken: keycloak.idToken,
      }),
    );
  };

  static readonly clear = (): void => localStorage.removeItem(OidcSession.attribute);
}

export const login = () => keycloak.login();

export const logout = () => {
  OidcSession.clear();
  keycloak.logout();
};

keycloak.onAuthLogout = logout;

const handleAuthenticated = async (): Promise<IAuthentication> => {
  OidcSession.put();
  const { name, orgcode, orgnaam } = keycloak.idTokenParsed!;
  const irisRoles = keycloak.resourceAccess?.[keycloak.clientId!]?.roles ?? [];
  return {
    authenticated: true,
    busyAuthenticating: false,
    authorised: irisRoles.length > 0 && !!orgcode,
    rollen: irisRoles,
    gebruiker: {
      naam: name,
      organisatie: orgnaam ?? orgcode ?? 'Onbekend',
    },
  };
};

const handleNotAuthenticated = (): IAuthentication => {
  OidcSession.clear();
  keycloak.clearToken();
  return {
    ...initialAuthentication,
    busyAuthenticating: false,
  };
};

export async function checkAuthenticated(): Promise<IAuthentication> {
  const authenticated = await keycloak.init({
    onLoad: 'check-sso',
    ...OidcSession.get(),
  });
  return authenticated ? handleAuthenticated() : handleNotAuthenticated();
}

async function fetchKeycloakToken(): Promise<string | undefined> {
  try {
    await keycloak.updateToken(30);
    OidcSession.put();
  } catch (e) {
    console.error('Token refresh failed', e);
  }
  return keycloak.token;
}

const authenticationRequestInterceptorOnFulfilled = (config: InternalAxiosRequestConfig<any>) =>
  fetchKeycloakToken().then((token) => {
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return Promise.resolve(config);
  });
const authenticationRequestInterceptorOnRejected = (error: any) => Promise.reject(error);

axios.interceptors.request.use(authenticationRequestInterceptorOnFulfilled, authenticationRequestInterceptorOnRejected);

const axiosResponseInterceptorOnFulfilled = (response: AxiosResponse) => response;
const axiosResponseInterceptorOnRejected = (error: any) => Promise.reject(error);

axios.interceptors.response.use(axiosResponseInterceptorOnFulfilled, axiosResponseInterceptorOnRejected);
